import PropTypes from "prop-types";
import React, { Children } from "react";
import classNames from "classnames";
import { LeftFilledChevronIcon, RightFilledChevronIcon } from "wm-graphics";
import { Slide } from "../../animations/Slide/Slide";
import { TextField } from "../TextField";
import { Text } from "../Text";
import { SelectedOption } from "../SelectField/SelectedOption/SelectedOption";
import { withTranslations } from "../../../utils/withTranslations";
import defaultLocales from "./locales";

import "./MultiSelect.scss";

const ENTER_KEY = "Enter";

export class UMultiSelect extends React.Component {
  static defaultProps = {
    defaultOpen: false,
    disabled: false,
  };

  static propTypes = {
    /** JSX objects to be rendered */
    children: PropTypes.node,
    /** Additional class names for the component */
    className: PropTypes.string,
    /** Initializes the multiselect to be open or closed */
    defaultOpen: PropTypes.bool,
    /** Initializes the multiselect with already selected values */
    defaultValue: PropTypes.array,
    /** Whether the select field's input can be changed */
    disabled: PropTypes.bool,
    /** Id associated with component */
    id: PropTypes.any,
    /** Label associated with component */
    label: PropTypes.string,
    /** Locales object for rendering text */
    locales: PropTypes.object,
    /** Callback function for when new option is selected */
    onClose: PropTypes.func,
    /** Callback function for when new option is selected */
    onChange: PropTypes.func,
    /** Callback function for when there is a search */
    onSearch: PropTypes.func,
    /** Message that displays when no options are selected */
    placeholder: PropTypes.string,
    /** If true, at least one option must be selected */
    requireSelect: PropTypes.bool,
    /** Sets the selected values so it can be controlled by outer component */
    value: PropTypes.array,
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedValues: props.defaultValue || props.value || [],
      isOpen: props.defaultOpen,
      searchValue: "",
    };

    if (props.defaultOpen) {
      document.addEventListener("mousedown", this.handleClickOutside);
    }

    this.fieldRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    this.updateSelectedValuesStateIfPropChanged(value, prevProps.value);
  }

  updateSelectedValuesStateIfPropChanged = (newValue, previousValue) => {
    if (newValue !== previousValue) {
      if (!newValue) {
        this.setState({ selectedValues: [] });
      } else {
        this.setState({ selectedValues: newValue });
      }
    }
  };

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.fieldRef &&
      this.fieldRef.current &&
      !this.fieldRef.current.contains(event.target)
    ) {
      this.setIsOpen(false);
    }
  };

  setIsOpen = (newIsOpen) => {
    const { id, onClose } = this.props;
    const { selectedValues } = this.state;

    if (newIsOpen) {
      document.addEventListener("mousedown", this.handleClickOutside);
    } else {
      document.removeEventListener("mousedown", this.handleClickOutside);
    }

    if (!newIsOpen) {
      if (onClose) {
        if (id) {
          onClose({ id, value: selectedValues });
        } else {
          onClose(selectedValues);
        }
      }
    }

    this.setState({ isOpen: newIsOpen });
  };

  toggleMenu = () => {
    const { disabled } = this.props;
    const { isOpen } = this.state;
    const newIsOpen = !isOpen;

    if (!disabled) {
      this.setIsOpen(newIsOpen);
    }
  };

  onKeydownToggleMenu = (event) => {
    if (event.key === ENTER_KEY) {
      this.toggleMenu();
    }
  };

  onOptionClick = (event) => {
    const { id, onChange } = this.props;
    const { selectedValues } = this.state;
    const value = event.currentTarget.getAttribute("value");
    if (!selectedValues.includes(value)) {
      selectedValues.push(value);
      this.setState({ selectedValues });
      if (onChange) {
        if (id) {
          onChange({ id, value: selectedValues });
        } else {
          onChange(selectedValues);
        }
      }
      this.onSearch("");
    }
  };

  onUnselectClick = (value) => {
    const { disabled, id, onChange, requireSelect } = this.props;
    const { selectedValues } = this.state;
    if (!(requireSelect && selectedValues.length === 1) && !disabled) {
      const newSelectedValues = selectedValues.filter(
        (selectedValue) => selectedValue !== value
      );
      this.setState({ selectedValues: newSelectedValues });
      if (onChange) {
        if (id) {
          onChange({ id, value: newSelectedValues });
        } else {
          onChange(newSelectedValues);
        }
      }
    }
  };

  onSearch = (value) => {
    const { onSearch } = this.props;
    if (onSearch) {
      onSearch(value);
    }
    this.setState({ searchValue: value });
  };

  renderSearch = () => {
    const { locales } = this.props;
    const { isOpen, searchValue } = this.state;

    return (
      <div className="textField">
        {isOpen ? (
          <TextField
            placeholder={locales.search}
            onChange={this.onSearch}
            defaultValue={searchValue}
            autoFocus
          />
        ) : (
          this.renderSelected()
        )}
      </div>
    );
  };

  renderOptions = () => {
    const { selectedValues } = this.state;
    const { children } = this.props;
    const childrenArray = Children.toArray(children);

    return childrenArray.map((option) => {
      if (selectedValues.includes(option.props.value)) {
        return React.cloneElement(option, {
          onUnselectClick: this.onUnselectClick,
          selected: true,
          key: option.props.value,
        });
      }

      return React.cloneElement(option, {
        onClick: this.onOptionClick,
        key: option.props.value,
      });
    });
  };

  renderSelected = () => {
    const { selectedValues } = this.state;
    const { children, locales, placeholder, requireSelect } = this.props;
    const childrenArray = Children.toArray(children);

    if (selectedValues.length === 0) {
      if (placeholder) {
        return <Text className="box__placeholder">{placeholder}</Text>;
      }
      return <Text className="box__placeholder">{locales.selectMultiple}</Text>;
    }

    return (
      <div className="box__selectedContainer">
        {childrenArray.map((option) => {
          if (selectedValues.includes(option.props.value)) {
            return (
              <SelectedOption
                value={option.props.value}
                key={option.props.value}
                onUnselectClick={this.onUnselectClick}
                requireSelect={requireSelect && selectedValues.length === 1}
              >
                {option.props.children}
              </SelectedOption>
            );
          }
          return undefined;
        })}
      </div>
    );
  };

  render() {
    const { className, disabled, id, label, onSearch } = this.props;
    const { isOpen } = this.state;

    const multiSelectClass = classNames("MultiSelect", className);
    const multiSelectBoxClass = classNames(
      "MultiSelect__box",
      { "MultiSelect__box--open": isOpen },
      { "MultiSelect__box--disabled": disabled }
    );

    return (
      <div className="MultiSelectContainer">
        {label && <label htmlFor={id}>{label}</label>}
        <div
          className={multiSelectClass}
          id={id}
          label={label}
          ref={this.fieldRef}
        >
          <div
            className={multiSelectBoxClass}
            onClick={this.toggleMenu}
            onKeyDown={this.onKeydownToggleMenu}
            role="button"
            tabIndex={0}
            data-testid="MultiSelect__box"
          >
            {onSearch ? this.renderSearch() : this.renderSelected()}
            <div className="MultiSelect__chevron">
              {isOpen ? (
                <LeftFilledChevronIcon
                  className="left-chevron"
                  width="10px"
                  height="10px"
                />
              ) : (
                <RightFilledChevronIcon
                  className="right-chevron"
                  width="10px"
                  height="10px"
                />
              )}
            </div>
          </div>
          <Slide isVisible={isOpen} timeout={100} unmountOnExit>
            <div className="MultiSelect__options">{this.renderOptions()}</div>
          </Slide>
        </div>
      </div>
    );
  }
}

const translatedMultiSelect = withTranslations(defaultLocales)(UMultiSelect);

export { translatedMultiSelect as MultiSelect };

export default translatedMultiSelect;
